import React from "react";
import { useWorkflowsBuilderPageView } from "../WorkflowsBuilderPage.context";
import {
  useGetNumbersQuery,
  useGetSmsAccountQuery,
  usePostSmsSegmentCalculatorMutation,
} from "../../../app/useGeneratedApi";
import { isNumberVerified, useSegmentCalculation } from "@tiny/sms/src/widgets";

export type SmsBuilderStep = "details" | "message";

export function useSmsCampaignBuilder(id: string) {
  const { updateNode, getNode, closeSmsBuilder } =
    useWorkflowsBuilderPageView();
  const node = getNode(id);

  const [step, setStep] = React.useState<SmsBuilderStep>("details");
  const [smsText, setSmsText] = React.useState(node?.data?.smsText || "");
  const [senderNumberId, setSenderNumberId] = React.useState(
    node?.data?.smsSenderNumberId
  );

  const { data: smsAccount, isLoading: accountLoading } =
    useGetSmsAccountQuery();
  const { data: allNumbers = [], isLoading: numbersLoading } =
    useGetNumbersQuery();

  // The workflow service sends from the account's default number (it reads no
  // sender from the node), so that is the only one offered. It must be
  // verified: carriers block texts from unverified toll-free numbers.
  const numbers = allNumbers.filter(
    (number) => number.phoneNumber === smsAccount?.phoneNumber
  );
  const senderVerified = numbers.some(isNumberVerified);
  const senderNumber = smsAccount?.phoneNumber ?? undefined;

  const { mutateAsync: calculateSegments } =
    usePostSmsSegmentCalculatorMutation();
  const { calculation, loading: calculationLoading } = useSegmentCalculation(
    smsText,
    calculateSegments
  );
  const messageValid = smsText.trim().length > 0;

  const persist = React.useCallback(() => {
    // An unverified sender can't send, so the step isn't ready yet. Until the
    // numbers and account load, verification is unknown: keep the saved flag.
    const configured =
      numbersLoading || accountLoading
        ? Boolean(node?.data?.config?.configured)
        : messageValid && senderVerified;
    const unchanged =
      smsText === (node?.data?.smsText || "") &&
      senderNumberId === node?.data?.smsSenderNumberId &&
      configured === Boolean(node?.data?.config?.configured);
    // Saving an untouched node would still autosave (and mark an active
    // workflow as having unpublished changes).
    if (unchanged) return;
    updateNode({
      id,
      data: {
        ...node?.data,
        smsText,
        smsSenderNumberId: senderNumberId,
        config: { ...node?.data?.config, configured },
      },
    });
  }, [
    id,
    node?.data,
    numbersLoading,
    accountLoading,
    messageValid,
    senderVerified,
    smsText,
    senderNumberId,
    updateNode,
  ]);

  const goToStep = (next: SmsBuilderStep) => {
    // The message step comes after a verified sender is picked.
    if (next === "message" && !senderVerified) return;
    persist();
    setStep(next);
  };

  const handleDone = () => {
    persist();
    closeSmsBuilder();
  };

  return {
    step,
    goToStep,
    smsText,
    setSmsText,
    senderNumberId,
    setSenderNumberId,
    smsAccount,
    numbers,
    numbersLoading,
    calculation,
    calculationLoading,
    senderNumber,
    messageValid,
    senderVerified,
    handleDone,
  };
}
